import React, { useCallback, useState } from 'react';
import { useRef } from 'react';
import { useContext } from 'react';
import { components } from 'react-select';
import Select from 'react-select';
import { store } from 'components/Store';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useSetFiltersToLocalStorageByType from 'components/Dashboard/Filters/useSetFiltersToLocalStorageByType';
import { colourStyles } from './SelectFilterUtils';
import CustomHighlighter from 'components/Dashboard/utilities/CustomHighlighter';

const emptyMessage = (subtype) => {
  switch (subtype) {
    case 'MembershipOfAPPGs':
      return 'No APPGs found';
    case 'ConsultationSource':
      return 'No sources found';
    case 'usStateOptions':
      return 'No state found';
    case 'DepartmentSelectType':
    case 'usDepartmentOptions':
      return 'No departments found';
    case 'usAgencyOptions':
      return 'No agencies found';
    case 'Teammates':
      return 'No team members found';
    case 'Clients':
      return 'No clients found';
    case 'CrmContacts':
      return 'No stakeholders found';
    case 'CrmInteractionTags':
      return 'No tags found';
    default:
      return 'No committees found';
  }
};

const placeholderText = (subtype) => {
  switch (subtype) {
    case 'MembershipOfAPPGs':
      return 'Search APPGs';
    case 'usStateOptions':
      return 'Search States';
    case 'ConsultationSource':
      return 'Search sources';
    case 'DepartmentSelectType':
    case 'usDepartmentOptions':
      return 'Search departments';
    case 'usAgencyOptions':
      return 'Search agencies';
    case 'Teammates':
      return 'Select teammates';
    case 'Clients':
      return 'Search clients';
    case 'CrmContacts':
      return 'Search stakeholders';
    case 'CrmInteractionTags':
      return 'Search tags';
    default:
      return 'Search committees';
  }
};

const optionType = (subtype) => {
  switch (subtype) {
    case 'Teammates':
      return 'checkbox';
    case 'Clients':
      return 'radio';
    default:
      return 'checkbox';
  }
};

const checkIfOptionSelected = (value, options, filterValue) => {
  if (filterValue) {
    return !!options.filter((option) => option.filterValue === filterValue && option.selected).length;
  }
  return !!options.filter((option) => option.name === value && option.selected).length;
};

const SelectOption = (props) => {
  const input = props.selectProps.inputValue;
  const option = props.data.label;
  const value = props.data.value;
  const { isSelected, isFocused } = props;
  const { optionType, options } = props;

  const optionRef = useCallback(
    (el) => {
      if (el) {
        const label = el.querySelector('label');
        const text = el.querySelector('label>span');

        let { width: labelWidth } = label.getBoundingClientRect() ?? {};
        let { width: spanWidth } = text.getBoundingClientRect() ?? {};
        let paddingLeft = 34;
        if (spanWidth > labelWidth - paddingLeft) {
          el.setAttribute('data-tooltip-class-name', `header-tooltip tooltip-width-min-content`);
          el.setAttribute('data-tooltip-content', option);
          el.setAttribute('data-tooltip-id', 'general-tooltip');
        }
      }
    },
    [input, option]
  );

  return (
    <div
      className={`email-component-select-option  ${
        checkIfOptionSelected(option, options, value) ? 'email-component-select-option-selected ' : ''
      }`}
      style={{
        backgroundColor: isSelected ? '#ebf7fb' : isFocused ? 'rgba(0, 148, 204, 0.04)' : 'transparent',
      }}
    >
      <components.Option {...props}>
        <div
          className={`${
            optionType === 'checkbox'
              ? 'checkbox-item'
              : 'radio-button specific-top-padding-4 specific-bottom-padding-8'
          } filter-item-${normalize(value)}`}
          key={option}
          ref={optionRef}
        >
          <input
            id={normalize(value)}
            onChange={() => {}}
            type={optionType}
            checked={checkIfOptionSelected(option, options, value)}
          />
          <label htmlFor={normalize(value)}>
            <CustomHighlighter highlightClassName='highlight-text' searchWord={input} textToHighlight={option} />
          </label>
        </div>
      </components.Option>
    </div>
  );
};

const SelectFilter = ({ disabled, filter, selectOption, useInPopUp, singleItem }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, clients } = state;
  const { type, options, subtype } = filter;
  const { setFiltersLocalStorageByType } = useSetFiltersToLocalStorageByType();
  const [hasSearched, setHasSearched] = useState(false);
  const [inputValue, setinputValue] = useState('');

  const teamMembersOptions = () => {
    return team?.allMembers
      ?.sort((a, b) => a.fullName.localeCompare(b.fullName))
      .map((member) => {
        return {
          label: `${member.fullName}`,
          value: `${member.id}`,
          hash: `${member.id}`,
        };
      });
  };

  const clientsOptions = () => {
    return clients?.map((client) => {
      return {
        label: `${client.teamName}`,
        value: `${client.teamId}`,
        hash: `${client.teamId}`,
      };
    });
  };

  const selectRef = useRef(null);

  const handleChange = (selected, action) => {
    if (action.option) {
      const lastSelected = {
        name: action.option.label,
        selected: true,
        filterValue: action.option.value,
        // Mirrors MembershipSelectFilter.jsx's own fix - without carrying this through,
        // filtersHashes.js falls back to hashing the display name instead of the stable id.
        hash: action.option.hash,
      };

      let valueToState = [];
      if (optionType(subtype) === 'checkbox') {
        valueToState = [...options, lastSelected];
      } else {
        valueToState = [lastSelected];
      }

      //remove previous value if exists
      if (checkIfOptionSelected(action.option.label, options, action.option.value)) {
        valueToState = valueToState.filter((option) => !(option.filterValue === action.option.value));
      }
      valueToState = valueToState.filter((option) => !(option.filterValue === action.option.value && !option.selected));
      selectOption({ filter, newValue: valueToState });

      if (!useInPopUp) {
        setFiltersLocalStorageByType({ type, options: valueToState });
      }
      setinputValue('');
    }
  };

  const optionsArray = () => {
    switch (subtype) {
      case 'Teammates':
        return teamMembersOptions();
      case 'Clients':
        return clientsOptions();
      default:
        return options ?? [];
    }
  };

  return (
    <div className='box-rounded mb-4 membership-main-container'>
      <div className='filter-select-container'>
        <span className='main-search-icon' />
        <Select
          ref={selectRef}
          options={optionsArray()}
          cacheOptions={true}
          components={{
            Option: (props) => <SelectOption {...props} optionType={optionType(subtype)} options={options} />,
            MultiValueContainer: () => null,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
          }}
          isMulti={!singleItem}
          openMenuOnFocus={true}
          noOptionsMessage={() => {
            if (!hasSearched) return null;
            return <>{emptyMessage(subtype)}</>;
          }}
          styles={colourStyles}
          placeholder={placeholderText(subtype)}
          onChange={handleChange}
          isDisabled={disabled}
          openMenuOnClick={true}
          hideSelectedOptions={false}
          classNamePrefix={'react-custom-select-contacts'}
          controlShouldRenderValue={false}
          onInputChange={(newValue, event) => {
            if (newValue.trim() === '') {
              setHasSearched(false);
            }
            if (event.action === 'input-change') {
              setinputValue(newValue);
            }
          }}
          closeMenuOnSelect={optionType(subtype) === 'radio'}
          inputValue={inputValue}
          menuShouldScrollIntoView={false}
        />
      </div>
    </div>
  );
};

export { emptyMessage, placeholderText, optionType, SelectOption, checkIfOptionSelected };
export default SelectFilter;
